import React, { useEffect, useState, useCallback } from 'react';
import SelfApprovedChip from '../common/SelfApprovedChip';
import { useParams, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import {
    Box,
    Button,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
} from '@mui/material';
import { StockTransferService } from '../../services/stockTransfer.service';
import {
    StockTransferDTO,
    STOCK_TRANSFER_STATUS_LABELS,
} from '../../interfaces/stockTransfer.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import ConfirmDialog from '../common/ConfirmDialog';
import DocumentSerialsCaption from '../serial/DocumentSerialsCaption';
import { useDocumentSerials } from '../serial/useDocumentSerials';

const StockTransferDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const toast = useToast();
    const [transfer, setTransfer] = useState<StockTransferDTO | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [actionInProgress, setActionInProgress] = useState<boolean>(false);
    const [confirming, setConfirming] = useState<'submit' | 'approve' | 'cancel' | null>(null);

    const canManage = usePermission(PERMISSIONS.STOCK_MANAGE);
    // F6b: the serials a completed transfer moved, listed under each line
    const lineSerials = useDocumentSerials(transfer?.id, 'TRANSFER_OUT', transfer?.status === 'COMPLETED');

    const fetchTransfer = useCallback(async () => {
        if (!id) return;
        try {
            setLoading(true);
            const data = await StockTransferService.getStockTransferById(id);
            setTransfer(data);
        } catch (err) {
            console.error('Error fetching stock transfer:', err);
            toast.showApiError(err, 'Failed to load stock transfer');
        } finally {
            setLoading(false);
        }
    }, [id, toast]);

    useEffect(() => {
        fetchTransfer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        return format(parseISO(dateString), 'dd-MM-yyyy');
    };

    const handleSubmitTransfer = async () => {
        if (!id) return;
        setConfirming(null);
        try {
            setActionInProgress(true);
            const updated = await StockTransferService.submitStockTransfer(id);
            setTransfer(prev => ({ ...updated, lines: updated.lines ?? prev?.lines ?? [] }));
            toast.showSuccess('Stock transfer submitted for approval');
        } catch (err) {
            console.error('Error submitting stock transfer:', err);
            toast.showApiError(err, 'Failed to submit stock transfer');
        } finally {
            setActionInProgress(false);
        }
    };

    const handleApprove = async () => {
        if (!id) return;
        setConfirming(null);
        try {
            setActionInProgress(true);
            const updated = await StockTransferService.approveStockTransfer(id);
            setTransfer(prev => ({ ...updated, lines: updated.lines ?? prev?.lines ?? [] }));
            toast.showSuccess('Stock transfer approved');
        } catch (err) {
            console.error('Error approving stock transfer:', err);
            toast.showApiError(err, 'Failed to approve stock transfer');
        } finally {
            setActionInProgress(false);
        }
    };

    const handleCancel = async () => {
        if (!id) return;
        setConfirming(null);
        try {
            setActionInProgress(true);
            const updated = await StockTransferService.cancelStockTransfer(id);
            setTransfer(prev => ({ ...updated, lines: updated.lines ?? prev?.lines ?? [] }));
            toast.showSuccess('Stock transfer cancelled');
        } catch (err) {
            console.error('Error cancelling stock transfer:', err);
            toast.showApiError(err, 'Failed to cancel stock transfer');
        } finally {
            setActionInProgress(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!transfer) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>Stock transfer not found.</Typography>
                <Button data-help="store.transfer.backNotFound" sx={{ mt: 2 }} variant="outlined" onClick={() => navigate('/stock-transfers')}>
                    Back to List
                </Button>
            </Box>
        );
    }

    const isDraft = transfer.status === 'DRAFT';
    const isPendingApproval = transfer.status === 'PENDING_APPROVAL';

    return (
        <Box sx={{ p: 3 }} data-help-page="store.transfer.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h4">Stock Transfer {transfer.referenceNo}</Typography>
                <Button data-help="store.transfer.back" variant="outlined" onClick={() => navigate('/stock-transfers')}>
                    Back to List
                </Button>
            </Box>

            <Paper elevation={3} sx={{ p: 3, mb: 3 }} data-help-section="store.transfer.header">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    <Box sx={{ minWidth: 160 }}>
                        <Typography variant="body2" color="text.secondary">From Store</Typography>
                        <Typography>{transfer.fromStoreName || '-'}</Typography>
                    </Box>
                    <Box sx={{ minWidth: 160 }}>
                        <Typography variant="body2" color="text.secondary">To Store</Typography>
                        <Typography>{transfer.toStoreName || '-'}</Typography>
                    </Box>
                    <Box sx={{ minWidth: 160 }}>
                        <Typography variant="body2" color="text.secondary">Status</Typography>
                        <Typography>
                            {STOCK_TRANSFER_STATUS_LABELS[transfer.status] ?? transfer.status}
                            <SelfApprovedChip selfApproved={transfer.selfApproved} rule={transfer.selfApprovalRule} />
                        </Typography>
                    </Box>
                    <Box sx={{ minWidth: 160 }}>
                        <Typography variant="body2" color="text.secondary">Transfer Date</Typography>
                        <Typography>{formatDate(transfer.transferDate)}</Typography>
                    </Box>
                    <Box sx={{ minWidth: 160 }}>
                        <Typography variant="body2" color="text.secondary">Approved Date</Typography>
                        <Typography>{formatDate(transfer.approvedDate)}</Typography>
                    </Box>
                    <Box sx={{ minWidth: 160 }}>
                        <Typography variant="body2" color="text.secondary">Completed Date</Typography>
                        <Typography>{formatDate(transfer.completedDate)}</Typography>
                    </Box>
                </Box>
                {transfer.notes && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="body2" color="text.secondary">Notes</Typography>
                        <Typography>{transfer.notes}</Typography>
                    </Box>
                )}
            </Paper>

            <TableContainer component={Paper} sx={{ mb: 3 }} data-help-section="store.transfer.lines">
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell data-help="store.transfer.col.no">#</TableCell>
                            <TableCell data-help="store.transfer.col.code">Item Code</TableCell>
                            <TableCell data-help="store.transfer.col.name">Item Name</TableCell>
                            <TableCell data-help="store.transfer.col.qty" align="right">Quantity</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(transfer.lines ?? []).length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    No lines
                                </TableCell>
                            </TableRow>
                        ) : (
                            (transfer.lines ?? []).map((line, index) => (
                                <TableRow key={line.id}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>{line.itemCode}</TableCell>
                                    <TableCell>
                                        {line.itemName}
                                        <DocumentSerialsCaption serials={lineSerials[line.itemId]} />
                                    </TableCell>
                                    <TableCell align="right">{line.quantity}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', gap: 2 }}>
                {isDraft && canManage && (
                    <Button
                        data-help="store.transfer.submit"
                        variant="contained"
                        onClick={() => setConfirming('submit')}
                        disabled={actionInProgress}
                    >
                        Submit for Approval
                    </Button>
                )}
                {isPendingApproval && canManage && (
                    <Button
                        data-help="store.transfer.approve"
                        variant="contained"
                        color="success"
                        onClick={() => setConfirming('approve')}
                        disabled={actionInProgress}
                    >
                        Approve
                    </Button>
                )}
                {(isDraft || isPendingApproval) && canManage && (
                    <Button
                        data-help="store.transfer.cancel"
                        variant="outlined"
                        color="error"
                        onClick={() => setConfirming('cancel')}
                        disabled={actionInProgress}
                    >
                        Cancel Transfer
                    </Button>
                )}
            </Box>

            <ConfirmDialog
                open={confirming !== null}
                title={
                    confirming === 'submit' ? 'Submit transfer?'
                        : confirming === 'approve' ? 'Approve transfer?'
                            : 'Cancel transfer?'
                }
                message={
                    confirming === 'submit'
                        ? 'The transfer will be sent for approval. It cannot be edited after this.'
                        : confirming === 'approve'
                            ? `Approving moves the stock from ${transfer.fromStoreName || 'the source store'} to ${transfer.toStoreName || 'the destination store'} straight away.`
                            : 'This transfer will be cancelled and no stock will move. This cannot be undone.'
                }
                confirmLabel={
                    confirming === 'submit' ? 'Submit' : confirming === 'approve' ? 'Approve' : 'Cancel transfer'
                }
                cancelLabel={confirming === 'cancel' ? 'Keep transfer' : 'Cancel'}
                confirmColor={confirming === 'cancel' ? 'error' : 'primary'}
                submitting={actionInProgress}
                onCancel={() => setConfirming(null)}
                onConfirm={() => {
                    if (confirming === 'submit') handleSubmitTransfer();
                    else if (confirming === 'approve') handleApprove();
                    else if (confirming === 'cancel') handleCancel();
                }}
            />
        </Box>
    );
};

export default StockTransferDetails;
